import React, { useEffect, useState, useMemo } from 'react';
import { QueryEvent, QueryTicketDto } from '@restore/queries/queries.types';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSiteUserQueriesSlice } from '@restore/siteUserQueries/siteUserQueries.slice';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { QueryPage } from 'src/components/Queries/QueryPage/QueryPage';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { QueryActionModal } from 'src/components/Queries/QueryActionModal';
import { QUERY_MODAL_CONFIG } from '@components/Queries/constants';
import { useSiteUserQueryHandlers } from '@components/Queries/useSiteUserQueryHandlers';
import { useQueryCapabilities } from '@components/Queries/flow/useQueryCapabilities';

export const SiteUserQueryPage: React.FC = () => {
  const { id: clinicalTrialId, queryId } = useParams<{
    id: string;
    queryId: string;
  }>();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('siteId');

  const [currentAction, setCurrentAction] = useState<QueryEvent | null>(null);

  const modalConfig = useMemo(
    () => (currentAction ? QUERY_MODAL_CONFIG[currentAction] : null),
    [currentAction]
  );

  const [
    loading,
    currentQuery,
    fetchCurrent,
    clearCurrent,
    currentQueryUpdating,
    updateCurrentQuery,
  ] = useSiteUserQueriesSlice((state, actions) => [
    state.currentQueryLoading,
    state.currentQuery,
    actions.fetchCurrentQuery,
    actions.clearCurrentQuery,
    state.currentQueryUpdating,
    actions.updateCurrentQuery,
  ]);

  const { handleDetailQuerySubmit, handlers } = useSiteUserQueryHandlers({
    ctId: clinicalTrialId!,
    siteId: siteId!,
    queryId,
    currentAction,
    updating: currentQueryUpdating,
    updateCurrentQuery,
  });

  const { canDo } = useQueryCapabilities(
    currentQuery as QueryTicketDto,
    handlers.selectedRole
  );

  const queryLabel = currentQuery?.queryNumber ? `Query ${currentQuery.queryNumber}` : 'Query ...';
  useBreadcrumbs([
    ['Site User Queries', 'subjectQueries'],
    [queryLabel, 'subjectQuery']
  ], [clinicalTrialId!, queryId!]);

  useEffect(() => {
    if (clinicalTrialId && queryId && siteId) {
      fetchCurrent({ clinicalTrialId, queryId, siteId });
    }
    return () => {
      clearCurrent();
    };
  }, [clinicalTrialId, queryId, siteId, fetchCurrent, clearCurrent]);

  if (!currentQuery) return null;

  return (
    <>
      <QueryPage
        loading={loading}
        query={currentQuery}
        isUpdating={currentQueryUpdating}
        triggerAction={setCurrentAction}
        topRightControls={
          canDo(QueryEvent.RESPOND) && (
            <Button
              type='primary'
              onClick={() => setCurrentAction(QueryEvent.RESPOND)}
            >
              Reply
            </Button>
          )
        }
      />
      <QueryActionModal
        open={!!currentAction}
        onClose={() => setCurrentAction(null)}
        title={modalConfig?.title}
        okText={modalConfig?.okText}
        textareaLabel={modalConfig?.textareaLabel}
        textareaPlaceholder={modalConfig?.textareaPlaceholder}
        messageRequiredMsg={modalConfig?.messageRequiredMsg}
        enableAttachments={modalConfig?.enableAttachments}
        subtitle={modalConfig?.subtitle}
        onSubmitData={handleDetailQuerySubmit}
        updating={currentQueryUpdating}
        credentialsRequired={currentAction !== QueryEvent.SEND_MESSAGE}
      />
    </>
  );
};
